/**
 * The answer key of a LITS grid is its shading. Kept apart from the form component so the
 * eagerly loaded module file never pulls the author's form into the player bundle.
 */
import { blankRows, resizeRows } from "./progress";
import { EMPTY, SHADED, type LitsDefinition, type LitsSolution } from "./types";

/**
 * What the core hands the form when nothing is queued: no rows at all, so the form can tell
 * "nothing typed" from a deliberately blank shading. `shadingOf` pads it to a blank grid.
 */
export function emptySolution(_definition: LitsDefinition): LitsSolution {
  return { rows: [] };
}

/** Whether a stored value is a shading at all, whatever its size. */
export function isShading(value: unknown): value is LitsSolution {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const rows = (value as { rows?: unknown }).rows;
  return Array.isArray(rows) && rows.every((row) => typeof row === "string" && /^[#.]*$/.test(row));
}

/** Nothing queued: not a shading, or the empty one the core hands out. */
export function isAbsent(value: unknown): boolean {
  return !isShading(value) || value.rows.length === 0;
}

/** Reads whatever is stored, however wrong, as a shading of the definition's size. */
export function shadingOf(value: unknown, definition: LitsDefinition): string[] {
  if (!isShading(value)) return blankRows(definition.rows, definition.cols);
  return resizeRows(
    value.rows.map((row) => Array.from(row, (c) => (c === SHADED ? SHADED : EMPTY)).join("")),
    definition.rows,
    definition.cols,
  );
}
